import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  authService,
  deliveryCityService,
  type DeliveryCity,
  type ListDeliveryCitiesParams,
} from '../services';
import { useToast } from '../hooks/useToast';

const BRAZILIAN_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

interface CityFormState {
  name: string;
  state: string;
  isActive: boolean;
}

const EMPTY_FORM: CityFormState = {
  name: '',
  state: '',
  isActive: true,
};

const DeliveryCitiesPage: React.FC = () => {
  const [cities, setCities] = useState<DeliveryCity[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [formState, setFormState] = useState<CityFormState>(EMPTY_FORM);
  const [editingCity, setEditingCity] = useState<DeliveryCity | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [cnpjFilter, setCnpjFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeliveryCity | null>(null);
  const { showError, showSuccess, showInfo } = useToast();

  const userRole = authService.getUserRole();
  const isDeveloper = userRole === 'DEVELOPER';

  const loadCities = useCallback(
    async (overrides?: Partial<ListDeliveryCitiesParams>) => {
      try {
        setLoading(true);
        const params: ListDeliveryCitiesParams = {
          searchTerm: searchTerm.trim() || undefined,
          state: stateFilter || undefined,
          isActive:
            statusFilter === 'all'
              ? undefined
              : statusFilter === 'active'
              ? true
              : false,
          ...(isDeveloper
            ? { cnpj: cnpjFilter.trim() || undefined }
            : {}),
          ...overrides,
        };

        const data = await deliveryCityService.list(params);
        setCities(data);
      } catch (error: any) {
        console.error('[DeliveryCitiesPage] loadCities error', error);
        showError(error.message || 'Não foi possível carregar as cidades.');
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, stateFilter, statusFilter, cnpjFilter, isDeveloper, showError],
  );

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  const handleOpenDialog = (city?: DeliveryCity) => {
    if (city) {
      setEditingCity(city);
      setFormState({
        name: city.name,
        state: city.state,
        isActive: city.isActive,
      });
    } else {
      setEditingCity(null);
      setFormState(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (dialogLoading) return;
    setDialogOpen(false);
    setEditingCity(null);
    setFormState(EMPTY_FORM);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      showInfo('Informe o nome da cidade.');
      return;
    }
    if (!formState.state) {
      showInfo('Selecione o estado (UF).');
      return;
    }

    try {
      setDialogLoading(true);
      if (editingCity) {
        const updated = await deliveryCityService.update(editingCity.id, {
          name: formState.name,
          state: formState.state,
          isActive: formState.isActive,
          cnpj: isDeveloper ? cnpjFilter.trim() || editingCity.cnpj : undefined,
        });
        showSuccess('Cidade atualizada com sucesso.');
        setCities((prev) => prev.map((city) => (city.id === updated.id ? updated : city)));
      } else {
        const created = await deliveryCityService.create({
          name: formState.name,
          state: formState.state,
          isActive: formState.isActive,
          cnpj: isDeveloper ? cnpjFilter.trim() || undefined : undefined,
        });
        showSuccess('Cidade cadastrada com sucesso.');
        setCities((prev) => [created, ...prev]);
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('[DeliveryCitiesPage] handleSubmit error', error);
      showError(error.message || 'Não foi possível salvar a cidade.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deliveryCityService.delete(deleteTarget.id, isDeveloper ? cnpjFilter.trim() || undefined : undefined);
      setCities((prev) => prev.filter((city) => city.id !== deleteTarget.id));
      showSuccess('Cidade removida com sucesso.');
    } catch (error: any) {
      console.error('[DeliveryCitiesPage] handleDelete error', error);
      showError(error.message || 'Não foi possível remover a cidade.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filteredCities = useMemo(() => cities, [cities]);

  const filtersActive = Boolean(
    searchTerm.trim() ||
      stateFilter ||
      statusFilter !== 'all' ||
      (isDeveloper && cnpjFilter.trim()),
  );

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Cidades de entrega
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 540, mt: 1 }}>
            Cadastre as cidades atendidas e controle o estado e a disponibilidade por CNPJ. Essas cidades
            são utilizadas como base para os bairros e taxas de entrega.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nova cidade
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            label="Buscar por nome"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            fullWidth
            InputProps={{
              endAdornment: <SearchIcon fontSize="small" color="action" />,}}
          />
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel id="state-filter-label">Estado</InputLabel>
            <Select
              labelId="state-filter-label"
              value={stateFilter}
              label="Estado"
              onChange={(event) => setStateFilter(event.target.value)}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {BRAZILIAN_STATES.map((uf) => (
                <MenuItem key={uf} value={uf}>
                  {uf}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Ativas</MenuItem>
              <MenuItem value="inactive">Inativas</MenuItem>
            </Select>
          </FormControl>
          {isDeveloper && (
            <TextField
              label="CNPJ alvo"
              value={cnpjFilter}
              onChange={(event) => setCnpjFilter(event.target.value.replace(/\D/g, '').slice(0, 14))}
              placeholder="Somente números"
              inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
              sx={{ minWidth: 220 }}
            />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={() => loadCities()}
            >
              Atualizar
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setStateFilter('');
                setStatusFilter('all');
                setCnpjFilter('');
                loadCities({ searchTerm: undefined, state: undefined, isActive: undefined, cnpj: undefined });
              }}
              disabled={!filtersActive}
            >
              Limpar filtros
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{filteredCities.length} cidade(s)</Typography>
          {loading && <CircularProgress size={22} />}
        </Box>
        <Divider />
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Cidade</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Atualização</TableCell>
                {isDeveloper && <TableCell>CNPJ</TableCell>}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCities.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={isDeveloper ? 6 : 5}>
                    <Box textAlign="center" py={5}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma cidade cadastrada com os filtros atuais.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {filteredCities.map((city) => (
                <TableRow hover key={city.id}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {city.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{city.state}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={city.isActive ? 'success' : 'default'}
                      label={city.isActive ? 'Ativa' : 'Inativa'}
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(city.updatedAt), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  {isDeveloper && <TableCell>{city.cnpj || '—'}</TableCell>}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Editar">
                        <IconButton color="primary" onClick={() => handleOpenDialog(city)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton color="error" onClick={() => setDeleteTarget(city)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        component="form"
        onSubmit={handleSubmit}
      >
        <DialogTitle>{editingCity ? 'Editar cidade' : 'Nova cidade'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {isDeveloper && (
              <TextField
                label="CNPJ"
                value={isDeveloper ? cnpjFilter : editingCity?.cnpj || ''}
                onChange={(event) => setCnpjFilter(event.target.value.replace(/\D/g, '').slice(0, 14))}
                helperText="Somente números. Se vazio, será usado o CNPJ autenticado."
                inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
                fullWidth
              />
            )}
            <TextField
              label="Nome da cidade"
              value={formState.name}
              onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel id="state-select-label">Estado (UF)</InputLabel>
              <Select
                labelId="state-select-label"
                value={formState.state}
                label="Estado (UF)"
                onChange={(event) => setFormState((prev) => ({ ...prev, state: event.target.value }))}
              >
                {BRAZILIAN_STATES.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.isActive}
                  onChange={(event) => setFormState((prev) => ({ ...prev, isActive: event.target.checked }))}
                  color="primary"
                />
              }
              label="Cidade ativa"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={dialogLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={dialogLoading}>
            {dialogLoading ? 'Salvando...' : editingCity ? 'Salvar alterações' : 'Cadastrar cidade'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover cidade</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Tem certeza de que deseja remover a cidade{' '}
            <Typography component="span" fontWeight={600}>
              {deleteTarget?.name}
            </Typography>
            ? Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeliveryCitiesPage;
